/**
 * GroupInfo — modal panel that opens when the group avatar is clicked.
 * Mirrors the VenueProfile card layout but uses GroupSoup palette + both
 * Comfortaa (body/UI) and Borsok (display headings / big numbers).
 */

import { useState } from 'react';
import styles from '../styles/Groupinfo.module.css';
import { useNavigate } from 'react-router-dom';

// ─── Types ────────────────────────────────────────────────────────────────────

interface GroupInfoProps {
  group: {
    id?: string;
    name: string;
    handle: string;
    avatar: string;
    description: string;
    memberCount: number;
    postCount: number;
    founded: string;
    tags: string[];
    isJoined: boolean;
    bannerUrl?: string;
    profile_picture_url?: string;
    rules?: string[];
    admins?: {
      name: string;
      avatar: string;
      role: 'Owner' | 'Admin' | 'Mod';
    }[];
  };
  onClose: () => void;
}

// ─── Component ────────────────────────────────────────────────────────────────

export default function GroupInfo({ group, onClose }: GroupInfoProps) {
  const navigate = useNavigate();
  const [showTags, setShowTags] = useState(false);
  const [showRules, setShowRules] = useState(false);
  const [reported, setReported] = useState(false);
  const [showMenu, setShowMenu] = useState(false);

  const handleReport = () => {
    setReported(true);
    setShowMenu(false);
  };

  const handleBackdrop = (e: React.MouseEvent<HTMLDivElement>) => {
    e.stopPropagation();
    if (e.target === e.currentTarget) onClose();
  };

  return (
    <div className={styles.backdrop} onClick={handleBackdrop}>
      <div className={styles.card}>
        {/* ── Banner ── */}
        {group.bannerUrl ? (
          <div className={styles.bannerWrap}>
            <img
              onLoad={(e) => e.currentTarget.classList.add('loaded')}
              src={group.bannerUrl}
              alt="Group banner"
              className={styles.bannerImg}
            />
          </div>
        ) : (
          <div className={styles.heroBanner} />
        )}

        {/* ── Floating controls (menu + close) ── */}
        <div className={styles.controls}>
          <div className={styles.menuWrap}>
            <button
              className={styles.iconBtn}
              onClick={() => setShowMenu((v) => !v)}
              aria-label="Options"
            >
              ⋮
            </button>
            {showMenu && (
              <div className={styles.dropdown}>
                <button
                  className={styles.dropItem}
                  onClick={handleReport}
                  disabled={reported}
                >
                  {reported ? '✓ Reported' : 'Report Group'}
                </button>
              </div>
            )}
          </div>

          <button className={styles.iconBtn} onClick={onClose} aria-label="Close">
            ✕
          </button>
        </div>

        {/* ── Card body ── */}
        <div className={styles.body}>
          <div className={styles.avatar}>
            {group.profile_picture_url ? (
              <img
                onLoad={(e) => e.currentTarget.classList.add('loaded')}
                className={styles.avatarImage}
                src={group.profile_picture_url}
                alt="Group avatar"
              />
            ) : (
              group.avatar
            )}
          </div>

          <h1 className={styles.groupName}>{group.name}</h1>
          <p className={styles.handle}>gs/{group.handle}</p>

          {/* Stats row */}
          <div className={styles.statsRow}>
            {[
              { value: group.memberCount.toLocaleString(), label: 'Members' },
              { value: group.postCount, label: 'Posts' },
              { value: group.founded, label: 'Founded' },
              { value: 'Public', label: 'Visibility' },
            ].map(({ value, label }) => (
              <div key={label} className={styles.stat}>
                <div className={styles.statVal}>{value}</div>
                <div className={styles.statLbl}>{label}</div>
              </div>
            ))}
          </div>

          {/* Description */}
          <div className={styles.infoField}>
            <span className={styles.fieldLabel}>About</span>
            <span className={styles.fieldValue}>{group.description}</span>
          </div>

          {/* Admins / mods */}
          {group.admins && group.admins.length > 0 && (
            <div className={styles.infoField}>
              <span className={styles.fieldLabel}>Leadership</span>
              <div className={styles.adminList}>
                {group.admins.map((admin) => (
                  <div key={admin.name} className={styles.adminRow}>
                    <div className={styles.adminAvatar}>{admin.avatar}</div>
                    <span className={styles.adminName}>{admin.name}</span>
                    <span
                      className={`${styles.roleBadge} ${
                        styles[admin.role.toLowerCase() as 'owner' | 'admin' | 'mod']
                      }`}
                    >
                      {admin.role}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Rules — collapsible */}
          {group.rules && group.rules.length > 0 && (
            <div className={styles.section}>
              <div className={styles.sectionHeader}>
                <span className={styles.fieldLabel}>Rules</span>
                <button
                  className={styles.toggleBtn}
                  onClick={() => setShowRules((v) => !v)}
                  aria-expanded={showRules}
                >
                  {showRules ? 'Hide' : 'View'}
                  <span
                    className={`${styles.chevron} ${showRules ? styles.chevronOpen : ''}`}
                  >
                    ▼
                  </span>
                </button>
              </div>
              <div
                className={`${styles.collapseBody} ${showRules ? styles.collapseOpen : ''}`}
              >
                <ol className={styles.ruleList}>
                  {group.rules.map((rule, i) => (
                    <li key={i} className={styles.ruleItem}>
                      <span className={styles.ruleNum}>{i + 1}</span>
                      <span className={styles.ruleText}>{rule}</span>
                    </li>
                  ))}
                </ol>
              </div>
            </div>
          )}

          {/* Tags — collapsible */}
          <div className={styles.section}>
            <div className={styles.sectionHeader}>
              <span className={styles.fieldLabel}>Interests</span>
              <button
                className={styles.toggleBtn}
                onClick={() => setShowTags((v) => !v)}
                aria-expanded={showTags}
              >
                {showTags ? 'Hide' : 'View'}
                <span
                  className={`${styles.chevron} ${showTags ? styles.chevronOpen : ''}`}
                >
                  ▼
                </span>
              </button>
            </div>
            <div
              className={`${styles.collapseBody} ${showTags ? styles.collapseOpen : ''}`}
            >
              {group.tags.length > 0 ? (
                <div className={styles.tagCloud}>
                  {group.tags.map((tag) => (
                    <span key={tag} className={styles.tag}>
                      {tag}
                    </span>
                  ))}
                </div>
              ) : (
                <p className={styles.emptyText}>No interests yet.</p>
              )}
            </div>
          </div>
        </div>
        <div className={styles.actionRow}>
          {group.id && (
            <button
              className={styles.primaryBtn}
              onClick={() => navigate(`/group/${group.id}`)}
            >
              View Group
            </button>
          )}

          {/* <button className={styles.secondaryBtn}>
           TODO: add joining functionality
          {group.isJoined ? 'Joined' : 'Join Group'}
        </button> */}
        </div>
      </div>
    </div>
  );
}
